import { isEqual, isWithinInterval } from 'date-fns';
import { selectEntities } from 'services/entities/selectors';
import { EntityId } from 'services/entities/types';
import { makeSelectHarvestSchemaEntityTypeId } from 'services/schemas/selectors';
import { selectSelectedSprayEventIdsForSubmission } from 'services/selectedSprayEventIdsForSubmission/selectors';
import { selectOrderedSprayEventsForSubmission } from 'services/sprayEventsForSubmission/selectors';
import {
    SprayEventForSubmission
} from 'services/sprayEventsForSubmission/types';
import { getSprayEventVarietiesArray } from 'services/sprayEvents/utils';
import { AppState } from 'services/types';
import { VarietyId } from 'services/varieties/types';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { makeSelectQueryStringNumberValue } from 'workspaces/react-router-selectors';
import { SPRAY_EVENT_IDS_TABLE_PAGINATION_NAME } from './constants';

import { OwnProps } from './index';

const selectStartDate = (
    state: AppState,
    props: OwnProps
): SprayEventForSubmission['startDate'] | undefined => props.startDate;
const selectEndDate = (
    state: AppState,
    props: OwnProps
): SprayEventForSubmission['endDate'] | undefined => props.endDate;
const selectEntityId = (
    state: AppState,
    props: OwnProps
): EntityId | undefined => props.entityId;
const selectVarietyId = (
    state: AppState,
    props: OwnProps
): VarietyId | undefined => props.varietyId;

const selectFilteredSprayEventIds = createDeepEqualSelector(
    selectOrderedSprayEventsForSubmission,
    selectStartDate,
    selectEndDate,
    selectEntityId,
    selectVarietyId,
    selectSelectedSprayEventIdsForSubmission,
    makeSelectHarvestSchemaEntityTypeId(),
    selectEntities,
    (
        sprayEvents,
        startDateFilter,
        endDateFilter,
        entityIdFilter,
        varietyIdFilter,
        selectedSprayEventIdsForSubmission,
        schemaEntityTypeId,
        entities
    ) =>
        sprayEvents
            .filter((event: SprayEventForSubmission) => {
                const { startDate, entityId, id, sprayEvent } = event;
                const { isReadyToSubmit, sprayEntities } = sprayEvent;

                if (selectedSprayEventIdsForSubmission.includes(id)) {
                    return false;
                }

                if (!isReadyToSubmit) {
                    return false;
                }

                if (entityIdFilter && entityIdFilter !== entityId) {
                    return false;
                }

                if (varietyIdFilter) {
                    const varieties = getSprayEventVarietiesArray(
                        sprayEntities,
                        schemaEntityTypeId,
                        entities
                    );

                    if (!varieties.includes(varietyIdFilter)) {
                        return false;
                    }
                }

                if (startDateFilter && endDateFilter) {
                    if (
                        !isWithinInterval(new Date(startDate), {
                            start: new Date(startDateFilter),
                            end: new Date(endDateFilter)
                        })
                    ) {
                        return false;
                    }
                } else if (startDateFilter) {
                    return isEqual(
                        new Date(startDate),
                        new Date(startDateFilter)
                    );
                } else if (endDateFilter) {
                    return isEqual(
                        new Date(startDate),
                        new Date(endDateFilter)
                    );
                }

                return true;
            })
            .map(({ id }) => id)
);

const makeSelectFilteredSprayEventIds = (): typeof selectFilteredSprayEventIds =>
    selectFilteredSprayEventIds;

const selectPaginatedDataIds = createDeepEqualSelector(
    [
        selectFilteredSprayEventIds,
        makeSelectQueryStringNumberValue(SPRAY_EVENT_IDS_TABLE_PAGINATION_NAME),
        makeSelectQueryStringNumberValue('count')
    ],
    (filteredIds, pageState: any = 1, countState: any = 10) => {
        const begin = (pageState - 1) * countState;
        const end = pageState * countState;
        return filteredIds.slice(begin, end);
    }
);

const makeSelectPaginatedDataIds = (): typeof selectPaginatedDataIds =>
    selectPaginatedDataIds;

const selectFilteredSprayEventIdsLength = createDeepEqualSelector(
    selectFilteredSprayEventIds,
    (sprayEventIds) => sprayEventIds.length || 0
);

const makeSelectFilteredSprayEventIdsLength = (): typeof selectFilteredSprayEventIdsLength =>
    selectFilteredSprayEventIdsLength;

export {
    makeSelectFilteredSprayEventIds,
    makeSelectFilteredSprayEventIdsLength,
    makeSelectPaginatedDataIds,
    selectStartDate,
    selectEndDate,
    selectEntityId
};
